"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { annonseraHelskarm } from "@/lib/widget-helskarm";

// Global fullscreen for interactive widgets. Every Explorer shares the outer
// frame `.interaktiv-widget` (the "Interaktiv" badge), so one layer gives all
// of them a fullscreen button instead of touching ~100 widget files — the
// same delegated pattern as TooltipLayer/OrdstodLayer.
//
// Fullscreen enlarges, it does not re-layout: the widget's content keeps the
// width it had on the page and is zoomed to fill the screen, so a teacher's
// walkthrough on the projector shows exactly what the students see, bigger.
// Where the Fullscreen API is missing (iPhone Safari) a fixed overlay stands in.

const WIDGET = ".interaktiv-widget";
const FALLBACK = "widget-helskarm-fallback";
// Room kept free around the zoomed content (px, screen space).
const MARGINAL_X = 96;
const MARGINAL_Y = 128;
const MAX_ZOOM = 3;

// Stable portal keys per widget element, so a widget appearing or leaving
// elsewhere on the page never remounts the other buttons.
const nycklar = new WeakMap<HTMLElement, string>();
let nastaNyckel = 0;
function nyckelFor(el: HTMLElement): string {
  let k = nycklar.get(el);
  if (!k) nycklar.set(el, (k = `hs-${nastaNyckel++}`));
  return k;
}

function aktivtHelskarmsElement(): Element | null {
  return document.fullscreenElement ?? document.querySelector(`.${FALLBACK}`);
}

function sattSkala(el: HTMLElement, bredd: number, hojd: number) {
  const zoom = Math.min(
    (window.innerWidth - MARGINAL_X) / bredd,
    (window.innerHeight - MARGINAL_Y) / hojd,
    MAX_ZOOM,
  );
  el.style.setProperty("--hs-zoom", String(Math.max(1, zoom)));
  el.style.setProperty("--hs-bredd", `${bredd}px`);
}

function rensaSkala(el: HTMLElement) {
  el.style.removeProperty("--hs-zoom");
  el.style.removeProperty("--hs-bredd");
}

function stangFallback(el: HTMLElement) {
  el.classList.remove(FALLBACK);
  rensaSkala(el);
  annonseraHelskarm(el, 1);
}

function aktuellZoom(el: HTMLElement): number {
  return Number(el.style.getPropertyValue("--hs-zoom")) || 1;
}

function HelskarmKnapp({ widget }: { widget: HTMLElement }) {
  const [aktiv, setAktiv] = useState(false);

  useEffect(() => {
    // Every button hears every fullscreen change; only a real change of this
    // widget's state is announced (and cleaned up).
    let varAktiv = false;
    function synka() {
      const nu = aktivtHelskarmsElement() === widget;
      if (nu === varAktiv) return;
      varAktiv = nu;
      setAktiv(nu);
      annonseraHelskarm(widget, nu ? aktuellZoom(widget) : 1);
      if (!nu) rensaSkala(widget);
    }
    document.addEventListener("fullscreenchange", synka);
    return () => document.removeEventListener("fullscreenchange", synka);
  }, [widget]);

  // Escape closes the overlay fallback (the real API handles Escape itself).
  useEffect(() => {
    if (!aktiv || document.fullscreenElement) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      stangFallback(widget);
      setAktiv(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [aktiv, widget]);

  function stang() {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
      return;
    }
    stangFallback(widget);
    setAktiv(false);
  }

  function oppnaFallback() {
    widget.classList.add(FALLBACK);
    setAktiv(true);
    annonseraHelskarm(widget, aktuellZoom(widget));
  }

  function oppna() {
    // Measure the frame's content box as laid out on the page, before
    // fullscreen widens it: that is the layout the zoom preserves. (Content
    // box, not the children: their margins are part of the layout.)
    const ruta = widget.getBoundingClientRect();
    const stil = getComputedStyle(widget);
    const px = (v: string) => parseFloat(v) || 0;
    const bredd = ruta.width - px(stil.paddingLeft) - px(stil.paddingRight) - px(stil.borderLeftWidth) - px(stil.borderRightWidth);
    const hojd = ruta.height - px(stil.paddingTop) - px(stil.paddingBottom) - px(stil.borderTopWidth) - px(stil.borderBottomWidth);
    sattSkala(widget, Math.max(bredd, 1), Math.max(hojd, 1));
    if (widget.requestFullscreen) {
      widget.requestFullscreen().catch(() => oppnaFallback());
    } else {
      oppnaFallback();
    }
  }

  return (
    <button
      type="button"
      data-helskarm-knapp
      onClick={aktiv ? stang : oppna}
      title={aktiv ? "Avsluta helskärm och gå tillbaka till sidan" : "Visa den interaktiva övningen i helskärm"}
      aria-label={aktiv ? "Avsluta helskärm" : "Visa i helskärm"}
      className="widget-helskarm-knapp"
    >
      {aktiv ? (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
        </svg>
      )}
      <span>{aktiv ? "Stäng" : "Helskärm"}</span>
    </button>
  );
}

export default function WidgetHelskarmLayer() {
  const [widgets, setWidgets] = useState<HTMLElement[]>([]);

  useEffect(() => {
    let frame = 0;
    function skanna() {
      frame = 0;
      const hittade = [...document.querySelectorAll<HTMLElement>(WIDGET)];
      setWidgets((forra) =>
        forra.length === hittade.length && forra.every((el, i) => el === hittade[i])
          ? forra
          : hittade,
      );
    }
    skanna();
    const observer = new MutationObserver(() => {
      if (!frame) frame = requestAnimationFrame(skanna);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return widgets.map((el) => createPortal(<HelskarmKnapp widget={el} />, el, nyckelFor(el)));
}
